import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { UploadCloud, FileText, Loader2, Sparkles, X, CheckSquare, File } from "lucide-react";

export default function DocumentAnalyzer({ onGenerateTasks }) {
  const [status, setStatus] = useState("idle"); // idle, analyzing, results
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      startAnalysis(e.dataTransfer.files[0].name);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      startAnalysis(e.target.files[0].name);
    }
  };

  const startAnalysis = (name) => {
    setFileName(name);
    setStatus("analyzing");
    
    // Simulate AI parsing and analysis delay
    setTimeout(() => {
      setStatus("results");
    }, 2500);
  };

  const handleGenerate = () => {
    setStatus("idle");
    onGenerateTasks(`Декомпозировать задачи на основе выводов из документа: ${fileName}`);
  };

  return (
    <>
      {/* Dropzone */}
      <div 
        className={`relative border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center transition-colors ${isDragging ? 'border-accent-500 bg-accent-500/5' : 'border-neutral-800 bg-neutral-900/20 hover:border-neutral-600 hover:bg-neutral-900/40'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input 
          type="file" 
          onChange={handleFileChange} 
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
        />
        
        {status === "idle" && (
          <>
            <div className="w-12 h-12 rounded-full bg-neutral-800/80 flex items-center justify-center mb-3">
              <UploadCloud className="w-6 h-6 text-neutral-400" />
            </div>
            <h3 className="text-sm font-medium text-neutral-200 mb-1">AI-Анализатор Документов</h3>
            <p className="text-xs text-neutral-500 text-center font-light max-w-sm">
              Перетащите файл (PDF, Word, Excel) сюда или кликните для загрузки.<br/>
              ИИ проанализирует контекст и подготовит выводы.
            </p>
          </>
        )}

        {status === "analyzing" && (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-accent-500 animate-spin" />
            <div className="text-sm text-neutral-300 font-medium">DeepSeek изучает документ...</div>
            <div className="text-xs text-neutral-500">Извлекаем сущности, риски и ключевые метрики из {fileName}</div>
          </div>
        )}
      </div>

      {/* Results Modal */}
      <AnimatePresence>
        {status === "results" && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setStatus("idle")}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-2xl bg-[#141414] border border-neutral-800 shadow-2xl rounded-xl overflow-hidden z-10"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-neutral-800/60 bg-[#1c1c1c]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-500/10 border border-accent-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-accent-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-neutral-100">Выводы AI по документу</h3>
                    <p className="text-xs text-neutral-500 mt-0.5 flex items-center gap-1.5">
                      <File className="w-3.5 h-3.5" /> {fileName}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setStatus("idle")}
                  className="text-neutral-500 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-neutral-300 uppercase tracking-wider">Главный вывод</h4>
                  <p className="text-sm text-neutral-400 font-light leading-relaxed bg-neutral-900/50 p-4 rounded-lg border border-neutral-800/50">
                    На основе предоставленного документа выявлено, что текущие процессы требуют доработки в части соответствия 152-ФЗ. Также обнаружены узкие места в скорости обработки входящих заявок (ожидание до 3 дней).
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-neutral-300 uppercase tracking-wider">Предлагаемый план действий (Чек-лист)</h4>
                  
                  <div className="flex items-start gap-3 p-3 bg-neutral-900 rounded-lg border border-neutral-800">
                    <CheckSquare className="w-5 h-5 text-accent-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm text-neutral-200 font-medium">Провести аудит безопасности данных</div>
                      <div className="text-xs text-neutral-500 mt-1">Определить потоки ПДн и проверить текущие регламенты их защиты.</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-neutral-900 rounded-lg border border-neutral-800">
                    <CheckSquare className="w-5 h-5 text-accent-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm text-neutral-200 font-medium">Оптимизировать воронку заявок</div>
                      <div className="text-xs text-neutral-500 mt-1">Внедрить автоматический скоринг для сокращения времени обработки до 1 часа.</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-neutral-900 rounded-lg border border-neutral-800">
                    <CheckSquare className="w-5 h-5 text-accent-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm text-neutral-200 font-medium">Привлечь внешнего юриста-аудитора</div>
                      <div className="text-xs text-neutral-500 mt-1">Необходима экспертиза для подписания актов по соответствию ФЗ.</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-neutral-800 bg-[#1c1c1c] flex justify-end gap-3">
                <button 
                  onClick={() => setStatus("idle")}
                  className="px-5 py-2.5 text-sm font-medium text-neutral-400 hover:text-white transition-colors"
                >
                  Отмена
                </button>
                <button 
                  onClick={handleGenerate}
                  className="flex items-center gap-2 bg-accent-600 hover:bg-accent-500 text-white px-6 py-2.5 rounded-lg transition-colors text-sm font-medium shadow-lg shadow-accent-900/20"
                >
                  <Sparkles className="w-4 h-4" />
                  Сгенерировать задачи из выводов
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
